import { FC } from 'react';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { useToast } from '@ordr-as/umami-react/toast';
import {
  type ErrorMessage,
  type TerminalDiagnosisResponse,
  TerminalDiagnosisResponseKind,
  useDiagnoseTerminal,
} from '@ordr-as/payment-service-types/epos';
import Spinner from '../../../../../../components/Spinner';
import { useLocation } from '../../../../../../context/LocationContext';

interface Props {
  onCloseDialog: () => void;
  pspTerminalId: string;
  terminalName: string;
  onTryPayment: () => void;
  canTryPaymentAnyway: boolean;
}

const getDiagnosisErrorMessage = (error: ErrorMessage) => error.message || 'Could not run terminal diagnostics';

const decodeTerminalText = (value: string) => {
  const normalizedValue = value.replace(/\+/g, ' ');

  try {
    return decodeURIComponent(normalizedValue);
  } catch {
    return normalizedValue;
  }
};

const renderBooleanSummary = (
  label: string,
  value: boolean | undefined,
  truthyLabel = 'OK',
  falsyLabel = 'Issue detected'
) => {
  if (value === undefined) {
    return null;
  }

  return (
    <li>
      <span className="font-semibold">{label}:</span> {value ? truthyLabel : falsyLabel}
    </li>
  );
};

const renderDiagnosisSummary = (
  diagnosisResult: TerminalDiagnosisResponse,
  terminalName: string,
  canTryPaymentAnyway: boolean
) => {
  switch (diagnosisResult.kind) {
    case TerminalDiagnosisResponseKind.DIAGNOSIS_RESPONSE:
      return (
        <div className="flex flex-col gap-4">
          <p>
            Live diagnostics completed for <b>{terminalName}</b>.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            {diagnosisResult.result && (
              <li>
                <span className="font-semibold">Result:</span> {diagnosisResult.result}
              </li>
            )}
            {diagnosisResult.errorCondition && (
              <li>
                <span className="font-semibold">Error condition:</span> {diagnosisResult.errorCondition}
              </li>
            )}
            {diagnosisResult.poiStatus && (
              <li>
                <span className="font-semibold">Terminal status:</span> {diagnosisResult.poiStatus.globalStatus}
              </li>
            )}
            {diagnosisResult.poiStatus?.printerStatus && (
              <li>
                <span className="font-semibold">Printer:</span> {diagnosisResult.poiStatus.printerStatus}
              </li>
            )}
            {renderBooleanSummary('Communication', diagnosisResult.poiStatus?.communicationOk)}
            {renderBooleanSummary('Card reader', diagnosisResult.poiStatus?.cardReaderOk)}
            {renderBooleanSummary('PED', diagnosisResult.poiStatus?.pedOk)}
            {renderBooleanSummary('Security', diagnosisResult.poiStatus?.securityOk)}
            {renderBooleanSummary(
              'Fraud prevention',
              diagnosisResult.poiStatus?.fraudPrevention,
              'Enabled',
              'Disabled'
            )}
          </ul>
          {diagnosisResult.hostStatus.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="font-semibold">Host reachability</p>
              <ul className="list-disc pl-5 space-y-1">
                {diagnosisResult.hostStatus.map((hostStatus, index) => (
                  <li key={`${hostStatus.acquirerId || 'host'}-${index}`}>
                    <span className="font-semibold">{hostStatus.acquirerId || 'Host'}:</span>{' '}
                    {hostStatus.isReachable ? 'Reachable' : 'Unreachable'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {diagnosisResult.loggedSaleIds.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="font-semibold">Logged sale IDs</p>
              <p className="break-words text-sm">{diagnosisResult.loggedSaleIds.join(', ')}</p>
            </div>
          )}
          {diagnosisResult.additionalResponse && (
            <div className="flex flex-col gap-1">
              <p className="font-semibold">Additional response</p>
              <p className="break-words whitespace-pre-wrap text-sm">
                {decodeTerminalText(diagnosisResult.additionalResponse)}
              </p>
            </div>
          )}
          <p className="text-sm">
            {canTryPaymentAnyway
              ? 'You can close this dialog, or try the payment anyway if the terminal now looks ready.'
              : 'You can close this dialog after reviewing the diagnostics.'}
          </p>
        </div>
      );
    case TerminalDiagnosisResponseKind.EVENT_NOTIFICATION:
      return (
        <div className="flex flex-col gap-4">
          <p>
            Adyen did not return a full diagnosis for <b>{terminalName}</b>. It returned a terminal event instead.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <span className="font-semibold">Event:</span> {diagnosisResult.eventNotification?.eventToNotify}
            </li>
            {diagnosisResult.eventNotification?.eventDetails && (
              <li>
                <span className="font-semibold">Details:</span>{' '}
                {decodeTerminalText(diagnosisResult.eventNotification.eventDetails)}
              </li>
            )}
            <li>
              <span className="font-semibold">Maintenance required:</span>{' '}
              {diagnosisResult.eventNotification?.maintenanceRequired ? 'Yes' : 'No'}
            </li>
            {diagnosisResult.eventNotification?.timestamp && (
              <li>
                <span className="font-semibold">Timestamp:</span> {diagnosisResult.eventNotification.timestamp}
              </li>
            )}
          </ul>
          <p className="text-sm">
            {canTryPaymentAnyway
              ? 'This usually means the terminal is still unavailable. You can close this dialog, or try the payment anyway if someone at the terminal can confirm it is ready.'
              : 'This usually means the terminal is still unavailable. You can close this dialog after reviewing the terminal event.'}
          </p>
        </div>
      );
    case TerminalDiagnosisResponseKind.REQUEST_FAILURE:
      return (
        <div className="flex flex-col gap-4">
          <p>
            Diagnostics for <b>{terminalName}</b> did not complete.
          </p>
          <p>{diagnosisResult.failureMessage || 'The request to the terminal timed out or failed.'}</p>
          <p className="text-sm">
            {canTryPaymentAnyway
              ? 'You can close this dialog, or try the payment anyway if the terminal looks ready.'
              : 'You can close this dialog after reviewing the failure.'}
          </p>
        </div>
      );
    default:
      return null;
  }
};

const TerminalDiagnosisDialog: FC<Props> = ({
  onCloseDialog,
  pspTerminalId,
  terminalName,
  onTryPayment,
  canTryPaymentAnyway,
}) => {
  const { location } = useLocation();
  const toasts = useToast();
  const {
    mutate,
    data: diagnosisResult,
    isPending,
  } = useDiagnoseTerminal({
    mutation: {
      onError: (error) => {
        toasts.pushError({
          id: pspTerminalId + 'diagnosis-error',
          message: <span>{getDiagnosisErrorMessage(error)}</span>,
        });
      },
    },
  });

  const isShowingSummary = diagnosisResult !== undefined;

  const sendDiagnosisRequest = () => {
    if (isPending || isShowingSummary) {
      return;
    }

    mutate({ locationId: location.id, pspTerminalId });
  };

  const tryPaymentAnyway = () => {
    if (isPending) {
      return;
    }

    onCloseDialog();
    onTryPayment();
  };

  const getTitle = () => {
    if (isPending) {
      return 'Running terminal diagnostics';
    }

    if (isShowingSummary) {
      return 'Terminal diagnosis summary';
    }

    return 'Terminal unavailable';
  };

  return (
    <Dialog
      className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
      onClose={onCloseDialog}
      title={getTitle()}
      actionLabel={isPending || !canTryPaymentAnyway ? undefined : 'Try payment anyway'}
      onAction={isPending || !canTryPaymentAnyway ? undefined : tryPaymentAnyway}
      isDisabledAction={isPending}
      secondaryActionLabel={isPending ? undefined : isShowingSummary ? 'Close' : 'Run diagnostics'}
      onSecondaryAction={isPending ? undefined : isShowingSummary ? onCloseDialog : sendDiagnosisRequest}
      isDisabledSecondaryAction={isPending}
    >
      {isPending ? (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <Spinner className="w-8 h-8" />
          <p>
            Checking <b>{terminalName}</b> directly with the payment terminal.
          </p>
          <p className="text-sm">This can take up to a minute.</p>
        </div>
      ) : isShowingSummary && diagnosisResult ? (
        renderDiagnosisSummary(diagnosisResult, terminalName, canTryPaymentAnyway)
      ) : (
        <div className="flex flex-col gap-4">
          <p>
            Terminal <b>{terminalName}</b> is currently reported as unavailable.
          </p>
          <p>
            {canTryPaymentAnyway
              ? 'You can run live diagnostics, or try sending the payment anyway if the terminal looks ready.'
              : 'You can run live diagnostics to check the terminal directly.'}
          </p>
          <p className="text-sm">Diagnostics can take up to a minute to complete.</p>
        </div>
      )}
    </Dialog>
  );
};

export default TerminalDiagnosisDialog;
